import React, { useState, useEffect } from 'react';
import Header from "../components/Header";
import Footer from "../components/Footer";
import OptionGroup from "../components/OptionGroup";
import { shopifyFetch } from '../shopify/client';
import { GET_PRODUCT_BY_HANDLE, GET_PRODUCTS_RELATED } from '../shopify/queries';
import { useDispatch } from 'react-redux';
import { addToCart } from '../shopify/cartActions';

const Detail = (props) => {
    const handle = props.match.params.handle;

    const dispatch = useDispatch();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedImage, setSelectedImage] = useState(0);
    const [selectedOptions, setSelectedOptions] = useState({});
    const [selectedVariant, setSelectedVariant] = useState(null);

    // Fetch main product
    useEffect(() => {
        setLoading(true);
        setError(null);

        shopifyFetch(GET_PRODUCT_BY_HANDLE, { handle })
            .then((res) => {
                const p = res.data.productByHandle;
                if (!p) { setError('Product not found.'); return; }

                setProduct(p);

                // Initialise selected options to the first value of each option
                const defaults = {};
                p.options.forEach((opt) => {
                    defaults[opt.name] = opt.values[0];
                });
                setSelectedOptions(defaults);

                // Fetch related products using the first tag
                return shopifyFetch(GET_PRODUCTS_RELATED, { query: p.title.split(' ')[0] });
            })
            .then((res) => {
                if (!res) return;
                const items = res.data.products.edges
                    .map((e) => e.node)
                    .filter((p) => p.handle !== handle); // exclude current product
                setRelated(items.slice(0, 4));
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [handle]);

    // Resolve matching variant whenever selectedOptions changes
    useEffect(() => {
        if (!product) return;

        const variants = product.variants.edges.map((e) => e.node);
        const match = variants.find((variant) =>
            variant.selectedOptions.every(
                (opt) => selectedOptions[opt.name] === opt.value
            )
        );
        setSelectedVariant(match || variants[0]);
    }, [selectedOptions, product]);

    const handleOptionChange = (name, value) => {
        setSelectedOptions((prev) => ({ ...prev, [name]: value }));
    };

    if (loading) return (
        <>
            <Header />
            <div className="container mt-5"><p>Loading product...</p></div>
            <Footer />
        </>
    );

    if (error) return (
        <>
            <Header />
            <div className="container mt-5"><p className="text-danger">{error}</p></div>
            <Footer />
        </>
    );

    const images = product.images.edges.map((e) => e.node);
    const price = selectedVariant
        ? parseFloat(selectedVariant.priceV2.amount).toFixed(2)
        : '—';
    const currency = selectedVariant?.priceV2.currencyCode || '';

    return (
        <>
            <Header />

            <div className="container" id="product-detail">
                <div className="row mt-5">
                    <div className="col-12 col-md-6">
                        <img
                            className="img-fluid mb-2"
                            src={images[selectedImage]?.url || 'assets/images/sample-1.jpg'}
                            alt={images[selectedImage]?.altText || product.title}
                        />

                        {images.length > 1 && (
                            <div className="d-flex mt-2" style={{ gap: '8px' }}>
                                {images.map((img, i) => (
                                    <img
                                        key={i}
                                        src={img.url}
                                        alt={img.altText}
                                        onClick={() => setSelectedImage(i)}
                                        style={{
                                            width: '60px',
                                            height: '60px',
                                            objectFit: 'cover',
                                            cursor: 'pointer',
                                            border: i === selectedImage ? '2px solid #000' : '2px solid transparent'
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="col-12 col-md-6 mt-3">
                        <div className="mb-4">
                            <h3>{product.title}</h3>
                            <h5>{currency} {price}</h5>
                            {selectedVariant && !selectedVariant.availableForSale && (
                                <span className="badge badge-secondary">Out of Stock</span>
                            )}
                        </div>

                        {product.options.map((opt) => (
                            <OptionGroup
                                key={opt.name}
                                label={opt.name}
                                options={opt.values.map((v) => ({ label: v, value: v }))}
                                value={selectedOptions[opt.name] || ''}
                                onChange={(val) => handleOptionChange(opt.name, val)}
                            />
                        ))}

                        <div className="mb-4">
                            <h6>Description</h6>
                            <div dangerouslySetInnerHTML={{ __html: product.descriptionHtml }} />
                        </div>

                        <button
                            className="btn btn-primary btn-cart btn-block"
                            disabled={!selectedVariant?.availableForSale}
                            onClick={() => dispatch(addToCart(selectedVariant.id))}
                        >
                            {selectedVariant?.availableForSale ? 'ADD TO CART' : 'OUT OF STOCK'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Related Products */}
            {related.length > 0 && (
                <div className="container mb-5" id="product">
                    <div className="mb-4">
                        <hr />
                        <h5 id="tx-shop">MORE PRODUCTS</h5>
                    </div>
                    <div className="row">
                        {related.map((item) => {
                            const img = item.images.edges[0]?.node;
                            const relatedPrice = parseFloat(item.priceRange.minVariantPrice.amount).toFixed(2);
                            const relatedCurrency = item.priceRange.minVariantPrice.currencyCode;
                            return (
                                <div
                                    key={item.id}
                                    className="col-12 col-md-3"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => props.history.push(`/product-detail/${item.handle}`)}
                                >
                                    <img
                                        className="img-fluid mb-2"
                                        src={img?.url || 'assets/images/sample-2.jpg'}
                                        alt={img?.altText || item.title}
                                    />
                                    <h4>{item.title}</h4>
                                    <p>{relatedCurrency} {relatedPrice}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <Footer />
        </>
    );
};

export default Detail;
